import { lazy } from 'react';
import MainLayout from '../../layouts/main-layout';
import { DOCS_AUTH_PATH } from '~/utils/docs-handshake';

const RegistrationPage = lazy(() => import('../../pages/registration/registration'));
const GuestLoginPage = lazy(() => import('../../pages/guest-login'));
const ResetPasswordPage = lazy(() => import('../../pages/reset-password'));
const TwoFactorLoginPage = lazy(() => import('../../pages/two-factor-login'));
const DocsAuthPage = lazy(() => import('../../pages/docs-auth'));
const AuthMicrosoftPage = lazy(() => import('../../pages/auth-microsoft'));

const ProjectsPage = lazy(() => import('../../pages/projects'));
const ProjectCRUDPage = lazy(() => import('../../pages/project-crud'));
const ProjectCRUDSidebar = lazy(() => import('../../pages/project-crud/sidebar'));
const ProjectPage = lazy(() => import('../../pages/project'));
const ProjectSidebar = lazy(() => import('../../pages/project/sidebar'));

const AssetPage = lazy(() => import('../../pages/asset'));

const SharesPage = lazy(() => import('../../pages/shares'));
const ShareQueuePage = lazy(() => import('../../pages/share-queue'));
const ShareEditPage = lazy(() => import('../../pages/share-edit'));
const ShareViewPage = lazy(() => import('../../pages/share-view'));
const ShareFeedbackPage = lazy(() => import('../../pages/share-feedback'));
const SocialRequestQueuePage = lazy(() => import('../../pages/social-request-queue'));

const DownloadsPage = lazy(() => import('../../pages/downloads'));

const WorkRequestsPage = lazy(() => import('../../pages/work-requests'));
const WorkRequestPage = lazy(() => import('../../pages/work-request-view'));
const WorkRequestQueuePage = lazy(() => import('../../pages/work-request-queue'));

const SubtitlerPage = lazy(() => import('../../pages/subtitler'));
const FileViewerPage = lazy(() => import('../../pages/file-viewer'));

const IngestsPage = lazy(() => import('../../pages/ingest'));

const ReportsPage = lazy(() => import('../../pages/reports'));

const AdminPage = lazy(() => import('../../pages/admin'));

/**
 * Root State. All states must inherit from `pixwel`. Disobey at your peril.
 */
const PixwelState = {
	name: 'pixwel',
	abstract: true,
	resolve: []
};

/**
 * Root state for logged in users. Any state that requires the user to
 * be logged in from must inherit from `pixwel.user`.
 */
const PixwelUserState = (resumeSession) => ({
	abstract: true,
	name: 'pixwel.user',
	views: {
		'root@': MainLayout
	},

	resolve: [
		{
			token: 'session',
			deps: [],
			resolveFn: () => {
				return resumeSession();
			}
		}
	]
});

const RegisterState = {
	name: 'register',
	parent: 'pixwel',
	url: '/register/{code:string}',
	views: {
		'root@': RegistrationPage
	}
};

const GuestLoginState = {
	name: 'guest-login',
	parent: 'pixwel',
	url: '/guest/{code:string}',
	views: {
		'root@': GuestLoginPage
	}
};

const ResetPasswordState = {
	name: 'reset-password',
	parent: 'pixwel',
	url: '/reset/{token:string}',
	views: {
		'root@': ResetPasswordPage
	}
};

const TwoFactorViewState = {
	name: 'twofactor',
	parent: 'pixwel',
	url: '/login/{token:string}',
	views: {
		'root@': TwoFactorLoginPage
	}
};

/**
 * Where Microsoft's sign-in popup is redirected back to. Unlike Google, Microsoft has no
 * `postmessage` shortcut, so the popup returns to a real URL — this one — which hands the
 * authorization code to the window that opened it and closes. No app chrome and no login gate:
 * it renders nothing and lives for a few milliseconds.
 *
 * The path is `REDIRECT_PATH` in `microsoft-identity-service`, and the same URL has to be
 * registered on the Entra app registration and in the API's `MICROSOFT_SSO_REDIRECT_URIS`.
 */
const AuthMicrosoftState = {
	name: 'auth-microsoft',
	parent: 'pixwel',
	url: '/auth/microsoft',
	views: {
		'root@': AuthMicrosoftPage
	}
};

/**
 * Documentation SSO hop. The Papervine docs site redirects an unauthenticated visitor here to
 * exchange their platform session for a docs JWT. Inherits from `pixwel.user` so the login gate
 * runs first, and renders into the root view (no app chrome) like `file-viewer` — it's a
 * transient redirect screen, not a destination.
 */
const DocsAuthState = {
	name: 'docs-auth',
	parent: 'pixwel.user',
	url: `${DOCS_AUTH_PATH}?{redirect:string}`,
	views: {
		'root@': DocsAuthPage
	},

	data: {
		title: 'Documentation'
	},

	params: {
		redirect: {
			value: '/',
			squash: true
		}
	}
};

const ProjectsState = {
	name: 'projects',
	parent: 'pixwel.user',
	url: '/?{archived:boolean}&{studios:list}&{searchText:string}',
	views: {
		main: ProjectsPage
	},

	data: {
		title: 'admin.menu.projects',
		stack: 'catalog'
	},

	dynamic: true,
	params: {
		searchText: {
			value: '',
			squash: true
		},
		category: {
			value: false,
			squash: true
		},
		studios: {
			value: [],
			squash: true,
			array: false
		}
	}
};

const ProjectNewState = {
	name: 'project-new',
	parent: 'pixwel.user',
	url: '/project/new',
	views: {
		main: ProjectCRUDPage,
		sidebar: ProjectCRUDSidebar
	},

	data: {
		title: 'Project',
		stack: 'catalog'
	}
};

const ProjectUpdateState = {
	name: 'project-update',
	parent: 'pixwel.user',
	url: '/project/:id/edit',
	views: {
		main: ProjectCRUDPage,
		sidebar: ProjectCRUDSidebar
	},

	data: {
		title: 'Project',
		stack: 'catalog'
	}
};

const EditAssetPageState = {
	name: 'asset.edit',
	url: '/edit?{tab:string}',
	views: {
		main: AssetPage,
		sidebar: ProjectSidebar
	},

	data: {
		title: 'Edit Asset',
		back: 'asset',
		stack: 'catalog'
	},

	dynamic: true,
	params: {
		tab: {
			value: 'details',
			squash: true
		}
	}
};

const AssetPageState = {
	name: 'asset',
	parent: 'pixwel.user',
	url: '/project/{projectId:string}/asset/{assetId:[^/]*}?{query:string}&{language:string}&{revision:string}&{usage:string}',
	views: {
		main: AssetPage,
		sidebar: ProjectSidebar
	},

	data: {
		title: 'Asset View',
		stack: 'catalog'
	},

	dynamic: true,
	params: {
		query: {
			value: '',
			squash: true
		},
		language: {
			value: '',
			squash: true
		},
		revision: {
			value: '',
			squash: true
		},
		usage: {
			value: '',
			squash: true
		}
	}
};

const ProjectState = {
	name: 'project',
	parent: 'pixwel.user',
	url: '/project/{projectId:[^/]*}?{query:string}&{types:list}&{status:string}&{access:string}&{length:int}&{language:string}&{private:boolean}&{creative:boolean}',
	views: {
		main: ProjectPage,
		sidebar: ProjectSidebar
	},

	data: {
		title: 'Assets',
		back: 'projects',
		stack: 'catalog'
	},

	dynamic: true,
	params: {
		query: {
			value: '',
			squash: true
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		status: {
			value: '',
			squash: true
		},
		access: {
			value: '',
			squash: true
		},
		length: {
			value: undefined,
			squash: true
		},
		language: {
			value: '',
			squash: true
		}
	}
};

const SharesPageState = {
	name: 'shares',
	parent: 'pixwel.user',
	url: '/shares?direction',
	views: {
		main: SharesPage
	},

	data: {
		title: 'Shares Dashboard',
		stack: 'shares'
	},

	params: {
		direction: {
			value: '',
			squash: true
		}
	}
};

const ShareNewState = {
	name: 'share-queue',
	parent: 'pixwel.user',
	url: '/shares/new',
	views: {
		main: ShareQueuePage
	},

	data: {
		title: 'Create Share',
		stack: 'share-request-queue'
	}
};

const ShareEditState = {
	name: 'share-edit',
	parent: 'pixwel.user',
	url: '/shares/edit/:id',
	views: {
		main: ShareEditPage
	},

	data: {
		title: 'Edit Share',
		stack: 'shares'
	}
};

const ShareViewState = {
	name: 'share-view',
	parent: 'pixwel.user',
	// `:index` is an optional 1-based playlist position used by deep links from
	// the share notification email (e.g. /shares/:id/1). Typed `int` so it only
	// matches numeric segments, and squashed so /shares/:id still matches and
	// selects the first asset.
	url: '/shares/:id/{index:int}?{query:string}&{language:string}&{revision:string}&{usage:string}&{direction:string}',
	views: {
		main: ShareViewPage
	},

	data: {
		title: 'View Share',
		stack: 'shares'
	},

	dynamic: true,
	params: {
		index: {
			value: null,
			squash: true
		},
		direction: {
			value: '',
			squash: true
		},
		query: {
			value: '',
			squash: true
		},
		language: {
			value: '',
			squash: true
		},
		revision: {
			value: '',
			squash: true
		},
		usage: {
			value: '',
			squash: true
		}
	}
};

const ShareFeedbackState = {
	name: 'share-feedback',
	parent: 'pixwel.user',
	url: '/shares/feedback/:id?{index:string}',
	views: {
		main: ShareFeedbackPage
	},

	data: {
		title: 'Share Feedback Report',
		stack: 'shares'
	},

	dynamic: true,
	params: {
		index: {
			value: '0'
		}
	}
};

const DownloadsState = {
	name: 'downloads',
	parent: 'pixwel.user',
	url: '/downloads',
	views: {
		main: DownloadsPage
	},

	data: {
		title: 'Downloads',
		stack: 'downloads'
	}
};

const WorkRequestsState = {
	name: 'work-requests',
	parent: 'pixwel.user',
	url: '/work-requests?{dates:string}&{studios:list}&{projects:list}&{groups:list}&{types:list}&{status:list}&{query:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: WorkRequestsPage
	},

	data: {
		title: 'Work Requests',
		stack: 'work-requests'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		status: {
			value: [],
			squash: true,
			array: false
		},
		query: {
			value: '',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'workRequest.created:desc',
			squash: true
		}
	}
};

const WorkRequestQueueState = {
	name: 'work-request-queue',
	parent: 'pixwel.user',
	url: '/work-requests/new?{assets:string}',
	views: {
		main: WorkRequestQueuePage
	},

	data: {
		title: 'Work Request Queue',
		stack: 'work-request-queue'
	},

	dynamic: true,
	params: {
		assets: {
			value: null,
			squash: true
		}
	}
};

const WorkRequestState = {
	name: 'work-request',
	parent: 'pixwel.user',
	url: '/work-requests/:id',
	views: {
		main: WorkRequestPage
	},

	data: {
		title: 'Work Request',
		stack: 'work-requests'
	}
};

const SubtitlerState = {
	name: 'subtitler',
	parent: 'pixwel.user',
	url: '/work-requests/:id/subtitler?{mode:string}&{type:string}&{language:string}',
	views: {
		main: SubtitlerPage
	},

	data: {
		title: 'Subtitler',
		stack: 'work-requests'
	},

	params: {
		mode: {
			value: '',
			squash: true
		},
		type: {
			value: '',
			squash: true
		},
		language: {
			value: '',
			squash: true
		}
	}
};

const FileViewerState = {
	name: 'file-viewer',
	parent: 'pixwel.user',
	url: '/file-viewer?{url:string}&{name:string}',
	// Render directly into the root view, overriding MainLayout, so the standalone
	// popup shows only the document — no app nav/sidebar chrome. Staying under
	// `pixwel.user` keeps the session/auth resolve in place.
	views: {
		'root@': FileViewerPage
	},

	data: {
		title: 'File Viewer'
	},

	params: {
		url: {
			value: '',
			squash: true
		},
		name: {
			value: '',
			squash: true
		}
	}
};

const IngestsState = {
	name: 'ingest',
	parent: 'pixwel.user',
	url: '/ingest?{query:string}&{archived:boolean}{dates:string}&{owner:string}&{status:list}&{project:list}&{asset:list}&{language:string}&{hasWorkRequest:boolean}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: IngestsPage
	},

	data: {
		title: 'Ingests',
		stack: 'ingest'
	},

	dynamic: true,
	params: {
		query: {
			value: '',
			squash: true
		},
		archived: {
			value: false,
			squash: true
		},
		owner: {
			value: '',
			squash: true
		},
		status: {
			value: [],
			squash: true,
			array: false
		},
		project: {
			value: [],
			squash: true,
			array: false
		},
		asset: {
			value: [],
			squash: true,
			array: false
		},
		language: {
			value: '',
			squash: true,
			array: false
		},
		hasWorkRequest: {
			value: null,
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'ingest.created:desc',
			squash: true
		}
	}
};

const IngestViewState = {
	name: 'ingest.view',
	url: '/:id?{tab:string}',
	views: {
		main: IngestsPage
	},

	data: {
		title: 'Manage Ingest',
		stack: 'ingest'
	},

	dynamic: true,
	params: {
		tab: {
			value: 'overview',
			squash: true
		}
	}
};

const SocialRequestQueueState = {
	name: 'social-request-queue',
	parent: 'pixwel.user',
	url: '/social-requests/new',
	views: {
		main: SocialRequestQueuePage
	},

	data: {
		title: 'Social Publish Queue',
		stack: 'social-request-queue'
	}
};

const AdminState = {
	name: 'admin',
	parent: 'pixwel.user',
	url: '/admin/{tab:.*}?{type:string}&{id:string}&{group:string}&{q:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: AdminPage
	},

	data: {
		title: 'Admin',
		stack: 'admin'
	},
	dynamic: true,
	params: {
		tab: {
			value: 'studios'
		},
		type: {
			value: '',
			squash: true
		},
		id: {
			value: '',
			squash: true
		},
		group: {
			value: '',
			squash: true
		},
		q: {
			value: '',
			squash: true
		}
	}
};

const ReportsState = {
	name: 'reports',
	parent: 'pixwel.user',
	url: '/reports',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports',
		stack: 'reports'
	}
};

const ReportsOrdersState = {
	name: 'reports-orders',
	parent: 'pixwel.user',
	url: '/reports/orders?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{states:list}&{groups:list}&{auto:string}&{language:list}&{customGroups:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Orders',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		states: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		auto: {
			value: '',
			squash: true
		},
		language: {
			value: [],
			squash: true,
			array: false
		},
		customGroups: {
			value: [],
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'workRequest.created:desc',
			squash: true
		}
	}
};

const ReportsOrderVolumeState = {
	name: 'reports-orderVolume',
	parent: 'pixwel.user',
	url: '/reports/order-volume?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{states:list}&{groups:list}&{autosubs:string}&{customGroups:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Order Volume',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		states: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		customGroups: {
			value: [],
			squash: true,
			array: false
		},
		autosubs: {
			value: '',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'workRequest.created:desc',
			squash: true
		}
	}
};

const ReportsOfflineRejectionState = {
	name: 'reports-offlineRejection',
	parent: 'pixwel.user',
	url: '/reports/offline-rejection?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{states:list}&{groups:list}&{autosubs:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Offline Rejection',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		states: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		autosubs: {
			value: '',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'workRequest.created:desc',
			squash: true
		}
	}
};

const ReportsEncodesState = {
	name: 'reports-encodes',
	parent: 'pixwel.user',
	url: '/reports/encodes?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{states:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Encodes',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		states: {
			value: [],
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'start:desc',
			squash: true
		}
	}
};

const ReportsGroupSpendingState = {
	name: 'reports-groupSpending',
	parent: 'pixwel.user',
	url: '/reports/group-spending?{dates:string}&{studios:list}&{projects:list}&{types:list}&{states:list}&{groups:list}&{autosubs:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Group Spending',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		states: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		autosubs: {
			value: '',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 999,
			squash: true
		},
		sort: {
			value: 'spending:desc',
			squash: true
		}
	}
};

const ReportsExceptionsState = {
	name: 'reports-exceptions',
	parent: 'pixwel.user',
	url: '/reports/exceptions?{dates:string}&{projects:list}&{types:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Exceptions',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		projects: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 999,
			squash: true
		},
		sort: {
			value: 'name:desc',
			squash: true
		}
	}
};

const ReportsDownloadsState = {
	name: 'reports-downloads',
	parent: 'pixwel.user',
	url: '/reports/downloads?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{groups:list}&{fileType:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Downloads',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		fileType: {
			value: '',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'downloaded:desc',
			squash: true
		}
	}
};

const ReportsFeedbackState = {
	name: 'reports-feedback',
	parent: 'pixwel.user',
	url: '/reports/feedback?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{groups:list}&{users:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Feedback',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		users: {
			value: [],
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'project.name:asc',
			squash: true
		}
	}
};

const ReportsGroupFeedbackState = {
	name: 'reports-groupFeedback',
	parent: 'pixwel.user',
	url: '/reports/group-feedback?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{groups:list}&{users:list}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Group Feedback',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		users: {
			value: [],
			squash: true,
			array: false
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'project.name:asc',
			squash: true
		}
	}
};

const ReportsSharesState = {
	name: 'reports-shares',
	parent: 'pixwel.user',
	url: '/reports/shares?{dates:string}&{studios:list}&{projects:list}&{assets:list}&{types:list}&{groups:list}&{recipients:list}&{guest:string}&{shareStatus:string}&{page:int}&{perPage:int}&{sort:string}',
	views: {
		main: ReportsPage
	},

	data: {
		title: 'Reports - Shares',
		stack: 'reports'
	},

	dynamic: true,
	params: {
		studios: {
			value: [],
			squash: true,
			array: false
		},
		projects: {
			value: [],
			squash: true,
			array: false
		},
		assets: {
			value: [],
			squash: true,
			array: false
		},
		types: {
			value: [],
			squash: true,
			array: false
		},
		groups: {
			value: [],
			squash: true,
			array: false
		},
		recipients: {
			value: [],
			squash: true,
			array: false
		},
		guest: {
			value: '',
			squash: true
		},
		shareStatus: {
			value: 'active',
			squash: true
		},
		page: {
			value: 1,
			squash: true
		},
		perPage: {
			value: 10,
			squash: true
		},
		sort: {
			value: 'created:desc',
			squash: true
		}
	}
};

export const routeStates = (resumeSession) => [
	PixwelState,
	PixwelUserState(resumeSession),
	RegisterState,
	GuestLoginState,
	ResetPasswordState,
	TwoFactorViewState,
	DocsAuthState,
	AuthMicrosoftState,
	AssetPageState,
	EditAssetPageState,
	ProjectsState,
	ProjectNewState,
	ProjectUpdateState,
	ProjectState,
	SharesPageState,
	ShareNewState,
	ShareEditState,
	ShareViewState,
	ShareFeedbackState,
	DownloadsState,
	WorkRequestsState,
	WorkRequestState,
	WorkRequestQueueState,
	SubtitlerState,
	FileViewerState,
	IngestsState,
	IngestViewState,
	SocialRequestQueueState,
	ReportsOrdersState,
	ReportsOrderVolumeState,
	ReportsOfflineRejectionState,
	ReportsEncodesState,
	ReportsGroupSpendingState,
	ReportsExceptionsState,
	ReportsDownloadsState,
	ReportsFeedbackState,
	ReportsGroupFeedbackState,
	ReportsSharesState,
	ReportsState,
	AdminState
];
